import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import {
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useQuery } from '@apollo/client';
import { AFFILIATE_STATS_QUERY } from '../../core/graphql/queries/affiliate';

const useStyles = makeStyles((theme) => ({
  statCard: {
    backgroundColor: '#1a2332',
    border: '1px solid #333',
    borderRadius: 8,
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
    },
  },
  statContent: {
    padding: theme.spacing(2),
  },
  statIcon: {
    fontSize: 32,
    marginBottom: theme.spacing(1),
  },
  statValue: {
    fontSize: '2rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  statLabel: {
    fontSize: '0.875rem',
    color: '#ccc',
  },
  trendUp: {
    color: '#4caf50',
  },
  trendDown: {
    color: '#f44336',
  },
  pending: {
    color: '#ff9800',
  },
  paid: {
    color: '#4caf50',
  },
  revenue: {
    color: '#2196f3',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  errorContainer: {
    textAlign: 'center',
    padding: theme.spacing(2),
    color: '#f44336',
  },
}));

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactElement;
  color: string;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  loading?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon,
  color,
  trend,
  loading = false,
}) => {
  const classes = useStyles();

  if (loading) {
    return (
      <Card className={classes.statCard}>
        <CardContent className={classes.statContent}>
          <Skeleton variant="rect" width="100%" height={120} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.statCard}>
      <CardContent className={classes.statContent}>
        <Box display="flex" flexDirection="column" alignItems="center" textAlign="center">
          <Box className={classes.statIcon} style={{ color }}>
            {icon}
          </Box>
          <Typography variant="h4" className={classes.statValue} style={{ color }}>
            {value}
          </Typography>
          <Typography variant="body2" className={classes.statLabel}>
            {title}
          </Typography>
          {trend && (
            <Box display="flex" alignItems="center" marginTop={1}>
              <TrendingUpIcon
                className={trend.isPositive ? classes.trendUp : classes.trendDown}
                fontSize="small"
              />
              <Typography
                variant="caption"
                className={trend.isPositive ? classes.trendUp : classes.trendDown}
                style={{ marginLeft: 4 }}
              >
                {trend.value}%
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

interface AffiliateStatsProps {
  showTitle?: boolean;
}

const AffiliateStats: React.FC<AffiliateStatsProps> = ({ showTitle = true }) => {
  const classes = useStyles();
  const { data, loading, error, refetch } = useQuery(AFFILIATE_STATS_QUERY, {
    pollInterval: 30000, // Poll every 30 seconds
    errorPolicy: 'all',
  });

  if (error) {
    return (
      <Box className={classes.errorContainer}>
        <Typography variant="h6" gutterBottom>
          Error Loading Stats
        </Typography>
        <Typography variant="body2">
          Unable to load affiliate statistics. Please try again later.
        </Typography>
      </Box>
    );
  }

  const stats = data?.affiliateStats || {
    totalConversions: 0,
    pendingCommissions: 0,
    paidCommissions: 0,
    totalRevenue: 0,
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Box>
      {showTitle && (
        <Typography variant="h5" style={{ color: '#fff', marginBottom: 24 }}>
          Performance Overview
        </Typography>
      )}
      
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Conversions"
            value={stats.totalConversions}
            icon={<CheckCircleIcon />}
            color="#4caf50"
            loading={loading}
            trend={{
              value: 12,
              isPositive: true,
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Commissions"
            value={formatCurrency(stats.pendingCommissions)}
            icon={<ScheduleIcon />}
            color="#ff9800"
            loading={loading}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Paid Commissions"
            value={formatCurrency(stats.paidCommissions)}
            icon={<MoneyIcon />}
            color="#4caf50"
            loading={loading}
            trend={{
              value: 8,
              isPositive: true,
            }}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Revenue"
            value={formatCurrency(stats.totalRevenue)}
            icon={<TrendingUpIcon />}
            color="#2196f3"
            loading={loading}
            trend={{
              value: 15,
              isPositive: true,
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AffiliateStats;
